import { ReactElement } from 'react'
import { buildErrorMessage } from '@constants'
import { AxiosError } from 'axios'
import {
  getGetOrganisationDefaultMemberCrossSectionsQueryKey,
  useGetOrganisationDefaultMemberCrossSections,
  useUpdateOrganisationDefaultMemberCrossSections,
} from '@api/default-member-cross-sections/default-member-cross-sections'
import type { DefaultMemberCrossSectionsDTO } from '@api/fastAPI.schemas'
import { useQueryClient } from '@tanstack/react-query'
import { useSnackbar } from '@ui/snackbar'
import DefaultMemberCrossSectionsEditor from './DefaultMemberCrossSectionsEditor'

interface Props {
  readOnly: boolean
}

const OrganisationDefaultMemberCrossSections = ({ readOnly }: Props): ReactElement => {
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const { data, isLoading } = useGetOrganisationDefaultMemberCrossSections<
    DefaultMemberCrossSectionsDTO,
    AxiosError
  >({
    query: {
      onError: error => {
        enqueueSnackbar(buildErrorMessage(error, 'Fehler beim Laden der Standardquerschnitte'), {
          variant: 'error',
        })
      },
    },
  })

  const { mutateAsync } = useUpdateOrganisationDefaultMemberCrossSections<AxiosError, unknown>({
    mutation: {
      meta: {
        title: 'Standardquerschnitte speichern',
        description: 'Ändert die Standardquerschnitte für Pfette, Unterzug und Stütze',
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries(getGetOrganisationDefaultMemberCrossSectionsQueryKey())
        enqueueSnackbar('Standardquerschnitte gespeichert', { variant: 'success' })
      },
      onError: error => {
        enqueueSnackbar(
          buildErrorMessage(error, 'Fehler beim Speichern der Standardquerschnitte'),
          { variant: 'error' },
        )
      },
    },
  })

  return (
    <DefaultMemberCrossSectionsEditor
      values={data}
      isLoading={isLoading}
      readOnly={readOnly}
      onSave={body => mutateAsync({ data: body })}
    />
  )
}

export default OrganisationDefaultMemberCrossSections
